import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { Copy, RotateCcw, Square, UserPlus } from "lucide-react"
import { useEffect, useMemo, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { memberName, plural } from "@/entities/audience"
import { getNiches } from "@/entities/category"
import {
	type Broadcast,
	BroadcastProgress,
	BroadcastStatusBadge,
	broadcastKeys,
	broadcastMediaSource,
	cancelBroadcast,
	duplicateBroadcast,
	formatWhen,
	updateBroadcast,
	processedCount,
	queuedCount,
	sentCount,
	totalOf,
} from "@/entities/broadcast"
import { DeleteButton, useRecipientCount } from "@/features/edit-broadcast-draft"
import { describeFilter } from "@/features/filter-audience"
import { BroadcastPreview } from "@/features/send-broadcast"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Card, CardContent } from "@/shared/ui/card"
import { useConfirm } from "@/shared/ui/confirm-provider"

function Tile({ label, value, tone }: { label: string; value: number; tone?: string }) {
	return (
		<div className="rounded-xl bg-muted/50 px-3 py-2.5">
			<p className="text-[11px] text-muted-foreground">{label}</p>
			<p className={cn("text-xl font-semibold tabular-nums", tone)}>{value}</p>
		</div>
	)
}

/** Запланированная или отправленная рассылка: статус, прогресс, итоги, недоставленные. */
export function BroadcastReport({ botId, broadcast }: { botId: string; broadcast: Broadcast }) {
	const niches = useQuery({ queryKey: ["niches"], queryFn: getNiches, staleTime: 5 * 60_000 })
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const key = broadcastKeys.detail(botId, broadcast.id)
	const mediaSource = useMemo(() => broadcastMediaSource(botId), [botId])
	const count = useRecipientCount(botId, broadcast.filter, broadcast.status === "SCHEDULED")
	const audience = totalOf(count.data)
	const { stats } = broadcast
	const processed = processedCount(stats)
	const percent = stats.total > 0 ? Math.round((processed / stats.total) * 100) : 0

	const cancel = useMutation({
		mutationFn: () => cancelBroadcast(botId, broadcast.id),
		onSuccess: async (next) => {
			queryClient.setQueryData(key, next)
			await queryClient.invalidateQueries({ queryKey: broadcastKeys.list(botId) })
		},
		onError: (error) =>
			toast.error(extractApiErrorMessage(error, "Не удалось остановить рассылку")),
	})
	const duplicate = useMutation({
		mutationFn: () => duplicateBroadcast(botId, broadcast.id),
		onSuccess: async (copy) => {
			queryClient.setQueryData(broadcastKeys.detail(botId, copy.id), copy)
			await queryClient.invalidateQueries({ queryKey: broadcastKeys.list(botId) })
			navigate(`/admin/bots/${botId}/broadcasts/${copy.id}`)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось сделать копию")),
	})

	// Тот же текст и кнопки, другие получатели: копия рассылки с новым фильтром.
	const followUp = useMutation({
		mutationFn: async ({ name, filter }: { name: string; filter: Broadcast["filter"] }) => {
			const copy = await duplicateBroadcast(botId, broadcast.id)
			return updateBroadcast(botId, copy.id, { name, filter })
		},
		onSuccess: async (copy) => {
			queryClient.setQueryData(broadcastKeys.detail(botId, copy.id), copy)
			await queryClient.invalidateQueries({ queryKey: broadcastKeys.list(botId) })
			navigate(`/admin/bots/${botId}/broadcasts/${copy.id}`)
		},
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось создать рассылку")),
	})
	const finished = broadcast.status === "DONE" || broadcast.status === "CANCELLED"
	const manual = broadcast.filter.sessionIds !== undefined
	const retryFailed = () =>
		followUp.mutate({
			name: `${broadcast.name}: повтор`,
			filter: { failedInBroadcastId: broadcast.id },
		})

	// Кнопка «Отправить ещё раз, где была ошибка» из итога в Telegram ведёт сюда с ?retry=failed.
	const [searchParams, setSearchParams] = useSearchParams()
	const retried = useRef(false)
	const retryRequested = searchParams.get("retry") === "failed"
	useEffect(() => {
		if (!retryRequested || retried.current) return
		retried.current = true
		setSearchParams({}, { replace: true })
		if (finished && stats.failed > 0) retryFailed()
	})

	const statusLine = (() => {
		switch (broadcast.status) {
			case "SCHEDULED":
				return broadcast.scheduledAt ? `Отправка ${formatWhen(broadcast.scheduledAt)}` : ""
			case "SENDING":
				return broadcast.startedAt ? `Отправляется с ${formatWhen(broadcast.startedAt)}` : ""
			case "DONE":
				return broadcast.finishedAt ? `Отправлена ${formatWhen(broadcast.finishedAt)}` : ""
			case "CANCELLED":
				return broadcast.finishedAt ? `Остановлена ${formatWhen(broadcast.finishedAt)}` : ""
			default:
				return ""
		}
	})()

	return (
		<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
			<div className="space-y-4">
				<Card className="border-border/60">
					<CardContent className="space-y-4 p-4">
						<div className="flex flex-wrap items-center gap-2">
							<BroadcastStatusBadge status={broadcast.status} />
							<span className="text-sm text-muted-foreground">{statusLine}</span>
						</div>

						{broadcast.status === "SCHEDULED" ? (
							<p className="text-sm">
								Сейчас под фильтр {plural(audience ?? 0, ["попадает", "попадают", "попадают"])}{" "}
								<b className="tabular-nums">{audience ?? "…"}</b>{" "}
								{plural(audience ?? 0, ["человек", "человека", "человек"])}. Точный список
								соберётся в момент отправки.
							</p>
						) : (
							<>
								<div className="space-y-1.5">
									<div className="flex justify-between text-xs text-muted-foreground">
										<span>
											{processed} из {stats.total}
										</span>
										<span>{percent}%</span>
									</div>
									<BroadcastProgress stats={stats} className="h-2" />
								</div>
								<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
									<Tile label="Отправлено" value={sentCount(stats)} tone="text-emerald-300" />
									<Tile label="Заблокировали бота" value={stats.blocked} tone="text-amber-300" />
									<Tile
										label="Ошибки"
										value={stats.failed + stats.noId}
										tone={stats.failed + stats.noId > 0 ? "text-red-300" : undefined}
									/>
									<Tile label="В очереди" value={queuedCount(stats)} />
								</div>
								{broadcast.status === "CANCELLED" && queuedCount(stats) > 0 ? (
									<p className="text-xs text-muted-foreground">
										{queuedCount(stats)}{" "}
										{plural(queuedCount(stats), ["пользователь", "пользователя", "пользователей"])} так и
										не получили сообщение.
									</p>
								) : null}
							</>
						)}

						<div className="flex flex-wrap gap-2">
							{broadcast.status === "SCHEDULED" ? (
								<Button
									variant="outline"
									className="rounded-xl"
									disabled={cancel.isPending}
									onClick={() => cancel.mutate()}
								>
									Снять с расписания
								</Button>
							) : null}
							{broadcast.status === "SENDING" ? (
								<Button
									variant="outline"
									className="rounded-xl text-red-300"
									disabled={cancel.isPending}
									onClick={() =>
										confirm({
											title: "Остановить рассылку?",
											message: `Ещё не отправлено: ${stats.pending}. Продолжить потом не получится: только сделать копию.`,
											confirmText: "Остановить",
											onConfirm: async () => {
												await cancel.mutateAsync().catch(() => undefined)
											},
										})
									}
								>
									<Square />
									Остановить
								</Button>
							) : null}
							{finished && stats.failed > 0 ? (
								<Button
									className="rounded-xl"
									disabled={followUp.isPending}
									title="Тот же текст. Получат только те, кому сообщение не дошло из-за ошибки"
									onClick={retryFailed}
								>
									<RotateCcw />
									Отправить ещё раз, где была ошибка
								</Button>
							) : null}
							{finished && !manual ? (
								<Button
									variant="outline"
									className="rounded-xl"
									disabled={followUp.isPending}
									title="Те же условия и текст. Получат только те, кому эта рассылка ещё не приходила, например пришедшие в бота после неё"
									onClick={() =>
										followUp.mutate({
											name: `${broadcast.name}: кто не получил`,
											filter: { ...broadcast.filter, excludeBroadcastId: broadcast.id },
										})
									}
								>
									<UserPlus />
									Отправить тем, кто не получил
								</Button>
							) : null}
							<Button
								variant="ghost"
								className="rounded-xl"
								disabled={duplicate.isPending}
								onClick={() => duplicate.mutate()}
							>
								<Copy />
								Сделать копию
							</Button>
						</div>
					</CardContent>
				</Card>

				{broadcast.failures.length > 0 ? (
					<Card className="overflow-hidden border-border/60">
						<p className="px-4 pt-3 text-sm font-semibold">Не доставлено</p>
						<ul className="divide-y divide-border/60">
							{broadcast.failures.map((failure) => (
								<li key={failure.id} className="px-4 py-2.5">
									<p className="truncate text-sm">
										{memberName({
											firstName: failure.profile?.firstName ?? null,
											lastName: failure.profile?.lastName ?? null,
											username: failure.profile?.username ?? null,
											telegramUserId: failure.telegramUserId,
										})}
									</p>
									<p className="truncate text-xs text-red-300/80">{failure.error}</p>
								</li>
							))}
						</ul>
					</Card>
				) : null}

				<Card className="border-border/60">
					<CardContent className="space-y-1 p-4">
						<p className="text-xs text-muted-foreground">Кому</p>
						<p className="text-sm">{describeFilter(broadcast.filter, broadcast.context.tags, niches.data)}</p>
					</CardContent>
				</Card>
			</div>

			<aside className="space-y-3 lg:sticky lg:top-4 lg:self-start">
				<BroadcastPreview
					botUsername={broadcast.context.botUsername}
					content={broadcast.content}
					mediaSource={mediaSource}
				/>
				<DeleteButton botId={botId} broadcast={broadcast} />
			</aside>
		</div>
	)
}
